import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Globe, Zap, Code, Users, ChevronRight, CheckCircle2 } from 'lucide-react';

const Home: React.FC = () => {
  return (
    <>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-emerald-600 to-green-500 text-white py-20">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-10 md:mb-0">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Reliable SMS OTP Verification for Your Applications
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-emerald-100">
                Fast, secure, and reliable SMS OTP service for developers and businesses worldwide.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/signup" 
                  className="bg-white text-emerald-700 px-8 py-3 rounded-md text-lg font-medium hover:bg-emerald-50 transition duration-300"
                >
                  Get Started
                </Link>
                <Link 
                  to="/docs" 
                  className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-md text-lg font-medium hover:bg-white/10 transition duration-300"
                >
                  Documentation
                </Link>
              </div>
            </div>
            <div className="md:w-1/2">
              <img 
                src="https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80" 
                alt="SMS OTP Verification" 
                className="w-full h-auto rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose getSMSOTP?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our SMS OTP service is designed to provide the most reliable and secure verification solution for your applications.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="bg-gray-50 p-8 rounded-xl shadow-sm hover:shadow-md transition duration-300">
              <ShieldCheck className="w-12 h-12 text-emerald-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Highly Secure</h3>
              <p className="text-gray-600">
                End-to-end encryption with compliance to global security standards to keep your users' data safe.
              </p>
            </div>
            
            <div className="bg-gray-50 p-8 rounded-xl shadow-sm hover:shadow-md transition duration-300">
              <Globe className="w-12 h-12 text-emerald-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Global Coverage</h3>
              <p className="text-gray-600">
                Send SMS OTPs to over 200 countries with local numbers for better deliverability and user trust.
              </p>
            </div>
            
            <div className="bg-gray-50 p-8 rounded-xl shadow-sm hover:shadow-md transition duration-300">
              <Zap className="w-12 h-12 text-emerald-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Lightning Fast</h3>
              <p className="text-gray-600">
                Deliver OTPs in seconds with our optimized infrastructure and global network of SMS providers.
              </p>
            </div>
            
            <div className="bg-gray-50 p-8 rounded-xl shadow-sm hover:shadow-md transition duration-300">
              <Code className="w-12 h-12 text-emerald-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Developer Friendly</h3>
              <p className="text-gray-600">
                Easy integration with RESTful APIs, SDKs for popular languages, and comprehensive documentation.
              </p>
            </div>
            
            <div className="bg-gray-50 p-8 rounded-xl shadow-sm hover:shadow-md transition duration-300">
              <Users className="w-12 h-12 text-emerald-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Scalable Solution</h3>
              <p className="text-gray-600">
                Whether you have 100 or 1 million users, our infrastructure scales to meet your needs without compromising performance.
              </p>
            </div>
            
            <div className="bg-gray-50 p-8 rounded-xl shadow-sm hover:shadow-md transition duration-300">
              <CheckCircle2 className="w-12 h-12 text-emerald-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Reliable Delivery</h3>
              <p className="text-gray-600">
                With multiple fallback providers and smart routing, we ensure your OTPs always reach their destination.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Implementing SMS OTP verification with getSMSOTP is simple and straightforward.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-emerald-100 text-emerald-700 w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">1</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Register & Integrate</h3>
              <p className="text-gray-600">
                Sign up for an account, get your API keys, and integrate our SDK or API into your application.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-emerald-100 text-emerald-700 w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">2</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Request OTP</h3>
              <p className="text-gray-600">
                When a user needs verification, your app calls our API to send an OTP to their phone number.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-emerald-100 text-emerald-700 w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">3</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Verify & Authenticate</h3>
              <p className="text-gray-600">
                User enters the OTP in your app, you verify it through our API, and authenticate the user.
              </p>
            </div>
          </div>
          
          <div className="mt-16 text-center">
            <Link 
              to="/docs" 
              className="inline-flex items-center text-emerald-600 hover:text-emerald-800 font-medium"
            >
              View Documentation <ChevronRight className="w-5 h-5 ml-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing Preview Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Simple, Transparent Pricing</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Pay only for what you use with no hidden fees or commitments.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="border border-gray-200 rounded-xl p-8 bg-white hover:shadow-lg transition duration-300">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Starter</h3>
              <p className="text-gray-600 mb-6">Perfect for small projects and startups</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">$19</span>
                <span className="text-gray-600">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">1,000 SMS OTPs per month</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Basic API access</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Email support</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">99.5% uptime SLA</span>
                </li>
              </ul>
              <Link 
                to="/signup" 
                className="block w-full bg-emerald-600 text-white text-center py-3 rounded-md hover:bg-emerald-700 transition duration-300"
              >
                Get Started
              </Link>
            </div>
            
            <div className="border-2 border-emerald-600 rounded-xl p-8 bg-white shadow-lg relative">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-emerald-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                MOST POPULAR
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Professional</h3>
              <p className="text-gray-600 mb-6">For growing businesses and applications</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">$49</span>
                <span className="text-gray-600">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">5,000 SMS OTPs per month</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Full API access</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Priority email support</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">99.9% uptime SLA</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Advanced analytics</span>
                </li>
              </ul>
              <Link 
                to="/signup" 
                className="block w-full bg-emerald-600 text-white text-center py-3 rounded-md hover:bg-emerald-700 transition duration-300"
              >
                Get Started
              </Link>
            </div>
            
            <div className="border border-gray-200 rounded-xl p-8 bg-white hover:shadow-lg transition duration-300">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Enterprise</h3>
              <p className="text-gray-600 mb-6">Custom solutions for large organizations</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">Custom</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Unlimited SMS OTPs</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Enterprise API access</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">24/7 phone and email support</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">99.99% uptime SLA</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Custom integrations</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Dedicated account manager</span>
                </li>
              </ul>
              <Link 
                to="/contact" 
                className="block w-full bg-gray-800 text-white text-center py-3 rounded-md hover:bg-gray-900 transition duration-300"
              >
                Contact Sales
              </Link>
            </div>
          </div>
          
          <div className="mt-12 text-center">
            <Link 
              to="/pricing" 
              className="inline-flex items-center text-emerald-600 hover:text-emerald-800 font-medium"
            >
              View Full Pricing Details <ChevronRight className="w-5 h-5 ml-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-emerald-600 text-white">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Secure Your Application?</h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-3xl mx-auto">
            Join thousands of developers who trust getSMSOTP for their verification needs. Get started today.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to="/signup" 
              className="bg-white text-emerald-700 px-8 py-3 rounded-md text-lg font-medium hover:bg-emerald-50 transition duration-300"
            >
              Create Free Account
            </Link>
            <Link 
              to="/contact" 
              className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-md text-lg font-medium hover:bg-white/10 transition duration-300"
            >
              Talk to Sales
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;